import { supabase } from '@/integrations/supabase/client';

// Check if current user is admin
export const checkAdminAccess = async () => {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  if (userError || !user) {
    throw new Error('User not authenticated');
  }

  // Check user role directly from user_profiles table
  const { data: profile, error: profileError } = await supabase
    .from('user_profiles')
    .select('role')
    .eq('id', user.id)
    .single();

  if (profileError) {
    console.error('Error checking user role:', profileError);
    throw new Error('Error verifying admin status.');
  }

  if (!profile || profile.role !== 'admin') {
    throw new Error('Access denied. Admin role required.');
  }

  return user;
};

// Get all users for admin management
// Note: AuthMiddleware handles admin access control, no need for additional checks
export const getAdminUserList = async () => {

  const { data, error } = await supabase
    .from('user_profiles')
    .select(`
      id,
      email,
      name,
      subscription_status,
      role,
      is_banned,
      created_at,
      updated_at,
      stripe_customer_id,
      stripe_subscription_id,
      plan_id,
      price_id,
      subscription_id,
      current_period_start,
      current_period_end,
      last_payment_date
    `)
    .order('created_at', { ascending: false });

  if (error) {
    console.error("Error fetching admin user list:", error);
    throw new Error(error.message || 'Failed to fetch admin user list.');
  }

  return data || [];
};

// Update user role
// Note: AuthMiddleware handles admin access control
export const updateUserRole = async (targetUserId: string, newRole: 'user' | 'admin') => {

  // Validate role
  if (!['user', 'admin'].includes(newRole)) {
    throw new Error('Invalid role. Must be user or admin.');
  }

  const { error } = await supabase
    .from('user_profiles')
    .update({ 
      role: newRole,
      updated_at: new Date().toISOString()
    })
    .eq('id', targetUserId);

  if (error) {
    throw new Error(error.message || 'Failed to update role.');
  }

  return {
    status: 200,
    message: 'User role updated successfully'
  };
};

// Update user details (subscription status, ban status)
export const updateUserDetails = async (
  targetUserId: string, 
  updates: {
    subscription_status?: string;
    is_banned?: boolean;
  }
) => {
  // Note: AuthMiddleware handles admin access control

  const updateData: any = { 
    updated_at: new Date().toISOString() 
  };

  if (updates.subscription_status !== undefined) {
    updateData.subscription_status = updates.subscription_status;
  }
  if (updates.is_banned !== undefined) {
    updateData.is_banned = updates.is_banned;
  }

  const { error } = await supabase
    .from('user_profiles')
    .update(updateData)
    .eq('id', targetUserId);

  if (error) {
    throw new Error(error.message || 'Failed to update user details.');
  }

  return {
    status: 200,
    message: 'User details updated successfully'
  };
};

// Reset user password (admin only)
// Note: AuthMiddleware handles admin access control
export const resetUserPassword = async (targetUserId: string, newPassword: string) => {

  if (!newPassword || newPassword.length < 6) {
    throw new Error('Password must be at least 6 characters long');
  }

  // Since we can't directly update passwords client-side, we'll need to use the admin API
  // This would typically be done server-side, but we can simulate it
  
  // For now, we'll create a password reset request
  const { data: targetProfile } = await supabase
    .from('user_profiles')
    .select('email')
    .eq('id', targetUserId)
    .single();

  if (!targetProfile?.email) {
    throw new Error('Target user not found');
  }

  // In a real implementation, you'd call your server endpoint here
  // For now, we'll simulate the password reset
  console.log(`Admin ${currentUser.id} requesting password reset for user ${targetUserId}`);
  
  // Since we can't actually reset passwords client-side, we'll send a password reset email
  const { error } = await supabase.auth.resetPasswordForEmail(targetProfile.email, {
    redirectTo: `${window.location.origin}/portal/auth?reset=true`
  });

  if (error) {
    throw new Error(error.message || 'Failed to send password reset email');
  }

  return {
    success: true,
    message: 'Password reset email sent successfully',
    userId: targetUserId
  };
};

// Plan management functions
// Note: AuthMiddleware handles admin access control
export const getPlans = async () => {

  const { data, error } = await supabase
    .from('plans')
    .select('*')
    .order('price');

  if (error) {
    throw new Error(error.message || 'Failed to fetch plans');
  }

  return data || [];
};

export const createPlan = async (planData: {
  name: string;
  description?: string;
  stripe_price_id: string;
  price: number;
  features: string[];
  max_chatbots: number;
  max_conversations_per_month?: number;
  is_active: boolean;
}) => {
  // Note: AuthMiddleware handles admin access control

  const { data, error } = await supabase
    .from('plans')
    .insert([{
      name: planData.name,
      description: planData.description || null,
      stripe_price_id: planData.stripe_price_id,
      price: planData.price,
      features: planData.features,
      max_chatbots: planData.max_chatbots,
      max_conversations_per_month: planData.max_conversations_per_month || null,
      is_active: planData.is_active,
    }])
    .select()
    .single();

  if (error) {
    throw new Error(error.message || 'Failed to create plan');
  }

  return data;
};

export const updatePlan = async (planId: string, planData: {
  name: string;
  description?: string;
  stripe_price_id: string;
  price: number;
  features: string[];
  max_chatbots: number;
  max_conversations_per_month?: number;
  is_active: boolean;
}) => {
  // Note: AuthMiddleware handles admin access control

  const { data, error } = await supabase
    .from('plans')
    .update({
      name: planData.name,
      description: planData.description || null,
      stripe_price_id: planData.stripe_price_id,
      price: planData.price,
      features: planData.features,
      max_chatbots: planData.max_chatbots,
      max_conversations_per_month: planData.max_conversations_per_month || null,
      is_active: planData.is_active,
      updated_at: new Date().toISOString(),
    })
    .eq('id', planId)
    .select()
    .single();

  if (error) {
    throw new Error(error.message || 'Failed to update plan');
  }

  return data;
};

export const deletePlan = async (planId: string) => {
  // Note: AuthMiddleware handles admin access control

  // Check if plan is in use
  const { data: subscriptions } = await supabase
    .from('subscriptions')
    .select('id')
    .eq('plan_id', planId)
    .eq('status', 'active')
    .limit(1);

  if (subscriptions && subscriptions.length > 0) {
    throw new Error('Cannot delete plan that has active subscriptions');
  }

  const { error } = await supabase
    .from('plans')
    .delete()
    .eq('id', planId);

  if (error) {
    throw new Error(error.message || 'Failed to delete plan');
  }

  return { success: true };
};

// User management utilities
export const banUser = async (userId: string) => {
  return updateUserDetails(userId, { is_banned: true });
};

export const unbanUser = async (userId: string) => {
  return updateUserDetails(userId, { is_banned: false });
};

export const suspendUserService = async (userId: string) => {
  return updateUserDetails(userId, { subscription_status: 'suspended' });
};

export const reactivateUserService = async (userId: string) => {
  return updateUserDetails(userId, { subscription_status: 'active' });
};

// Specific function for updating subscription status (for compatibility)
export const updateUserSubscriptionStatus = async (userId: string, status: string) => {
  return updateUserDetails(userId, { subscription_status: status });
};

// Simplified password reset function (for compatibility)
// Note: AuthMiddleware handles admin access control
export const resetUserPasswordSimple = async (userId: string) => {

  const { data: targetProfile } = await supabase
    .from('user_profiles')
    .select('email')
    .eq('id', userId)
    .single();

  if (!targetProfile?.email) {
    throw new Error('Target user not found');
  }

  // Send password reset email
  const { error } = await supabase.auth.resetPasswordForEmail(targetProfile.email, {
    redirectTo: `${window.location.origin}/portal/auth?reset=true`
  });

  if (error) {
    throw new Error(error.message || 'Failed to send password reset email');
  }

  return {
    success: true,
    message: 'Password reset email sent successfully',
    userId: userId
  };
};

// Create a new user (admin only)
// Note: AuthMiddleware handles admin access control
export const createUser = async (userData: {
  email: string;
  name: string;
  password: string;
  role: 'user' | 'admin';
  subscription_status?: string;
}) => {
  // Validate inputs
  if (!userData.email || !userData.name || !userData.password) {
    throw new Error('Email, name, and password are required');
  }

  if (!['user', 'admin'].includes(userData.role)) {
    throw new Error('Invalid role. Must be user or admin.');
  }

  if (userData.password.length < 6) {
    throw new Error('Password must be at least 6 characters long');
  }

  // Check if user already exists
  const { data: existingUser } = await supabase
    .from('user_profiles')
    .select('id')
    .eq('email', userData.email)
    .single();

  if (existingUser) {
    throw new Error('User with this email already exists');
  }

  // Create user in auth.users (this requires admin privileges)
  const { data: authUser, error: authError } = await supabase.auth.admin.createUser({
    email: userData.email,
    password: userData.password,
    email_confirm: true, // Auto-confirm email
    user_metadata: {
      name: userData.name,
      role: userData.role,
    },
  });

  if (authError) {
    throw new Error(authError.message || 'Failed to create user account');
  }

  if (!authUser.user) {
    throw new Error('User creation failed - no user returned');
  }

  // Create user profile
  const { error: profileError } = await supabase
    .from('user_profiles')
    .insert({
      id: authUser.user.id,
      email: userData.email,
      name: userData.name,
      role: userData.role,
      subscription_status: userData.subscription_status || 'free',
      is_banned: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });

  if (profileError) {
    // If profile creation fails, clean up the auth user
    await supabase.auth.admin.deleteUser(authUser.user.id);
    throw new Error(profileError.message || 'Failed to create user profile');
  }

  return {
    success: true,
    message: 'User created successfully',
    user: {
      id: authUser.user.id,
      email: userData.email,
      name: userData.name,
      role: userData.role,
    },
  };
};

// Delete user (admin only)
// Note: AuthMiddleware handles admin access control
export const deleteUser = async (targetUserId: string) => {
  // Validate user ID
  if (!targetUserId) {
    throw new Error('User ID is required');
  }

  // Get the current user to make sure they're not deleting themselves
  const { data: { user: currentUser } } = await supabase.auth.getUser();
  if (currentUser?.id === targetUserId) {
    throw new Error('Cannot delete your own account');
  }

  // Check if target user exists
  const { data: targetProfile } = await supabase
    .from('user_profiles')
    .select('id, email, role, subscription_status')
    .eq('id', targetUserId)
    .single();

  if (!targetProfile) {
    throw new Error('User not found');
  }

  // Check if user has active subscription
  const hasActiveSubscription = targetProfile.subscription_status === 'active' || targetProfile.subscription_status === 'trialing';
  if (hasActiveSubscription) {
    throw new Error('Cannot delete user with active subscription. Please cancel subscription first.');
  }

  // Additional safety: prevent deletion of other admins unless current user is admin
  const { data: currentProfile } = await supabase
    .from('user_profiles')
    .select('role')
    .eq('id', currentUser!.id)
    .single();

  if (targetProfile.role === 'admin' && currentProfile?.role !== 'admin') {
    throw new Error('Cannot delete admin users');
  }

  try {
    // Since auth.admin methods are not available client-side, we'll just delete the profile
    // This will disable the user but won't remove their auth record
    // In a production app, you'd want to do this through a server endpoint
    
    // Delete user profile and related data
    const { error: profileError } = await supabase
      .from('user_profiles')
      .delete()
      .eq('id', targetUserId);

    if (profileError) {
      console.error('Profile deletion error:', profileError);
      throw new Error(profileError.message || 'Failed to delete user profile');
    }

    // Also delete related data (chatbots, conversations, etc.)
    // These should cascade automatically if foreign keys are set up properly
    
    return {
      success: true,
      message: 'User profile deleted successfully. User account has been disabled.',
      userId: targetUserId,
    };
  } catch (error) {
    console.error('Delete user error:', error);
    throw new Error(error instanceof Error ? error.message : 'Failed to delete user');
  }
};

// Get user statistics for admin dashboard
// Note: AuthMiddleware handles admin access control
export const getUserStats = async () => {

  const { data: totalUsers } = await supabase
    .from('user_profiles')
    .select('id', { count: 'exact', head: true });

  const { data: activeSubscriptions } = await supabase
    .from('user_profiles')
    .select('id', { count: 'exact', head: true })
    .eq('subscription_status', 'active');

  const { data: bannedUsers } = await supabase
    .from('user_profiles')
    .select('id', { count: 'exact', head: true })
    .eq('is_banned', true);

  const { data: adminUsers } = await supabase
    .from('user_profiles')
    .select('id', { count: 'exact', head: true })
    .eq('role', 'admin');

  return {
    totalUsers: totalUsers?.length || 0,
    activeSubscriptions: activeSubscriptions?.length || 0,
    bannedUsers: bannedUsers?.length || 0,
    adminUsers: adminUsers?.length || 0,
  };
};
